import { Stack } from 'expo-router';
import { type ComponentProps, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import type ButtonToast from '@/components/Custom/ButtonToast';
import * as Header from '@/components/Custom/Header';
import InviteShareOptions from '@/components/Custom/InviteShareOptions';
import {
  type MemberType,
  MemberTypeSelector,
} from '@/components/People/PeopleFilterSelector';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';
import { graphql } from '@/gql/graphql';
import getOrganizationInviteLink from '@/utils/getOrganizationInviteLink';
import getTeamInviteLink from '@/utils/getTeamInviteLink';
import type { MemberAddEntity, MemberAddEntityKind } from './MemberAdd.types';

const MemberInviteCode = graphql(/* GraphQL */ `
  query MemberInviteCode($kind: invite_entry_kind!, $entityId: uuid!) {
    invite_entry_code(args: { kind: $kind, entity_id: $entityId }) {
      code
    }
  }
`);

const RotateMemberInviteCode = graphql(/* GraphQL */ `
  mutation RotateMemberInviteCode($kind: invite_entry_kind!, $entityId: uuid!) {
    rotate_invite_code(args: { kind: $kind, entity_id: $entityId }) {
      code
    }
  }
`);

const inviteVariants = {
  organization: {
    getLink: getOrganizationInviteLink,
    testIDPrefix: 'OrganizationPersonAdd',
  },
  team: {
    getLink: getTeamInviteLink,
    testIDPrefix: 'TeamPersonAdd',
  },
} satisfies Record<
  MemberAddEntityKind,
  {
    getLink: (
      inviteCode: string | null | undefined,
      memberType: MemberType,
    ) => string;
    testIDPrefix: string;
  }
>;

export default function MemberAddInvite({
  entity,
  initialMemberType,
  setToast,
}: {
  entity: MemberAddEntity;
  initialMemberType: MemberType;
  setToast: (toast: ComponentProps<typeof ButtonToast>) => void;
}) {
  const variant = inviteVariants[entity.kind];
  const [memberType, setMemberType] = useState(initialMemberType);
  const [rotatedInviteCode, setRotatedInviteCode] = useState<string | null>(
    null,
  );
  const [hasRefreshed, setHasRefreshed] = useState(false);
  const [{ data: inviteData, fetching: isFetchingInviteCode }] = useQuery({
    query: MemberInviteCode,
    variables: {
      entityId: entity.id,
      kind: entity.kind,
    },
    requestPolicy: 'network-only',
  });
  const [{ fetching: isRotating }, rotateInviteCode] = useMutation(
    RotateMemberInviteCode,
  );
  const inviteCode = (
    rotatedInviteCode || inviteData?.invite_entry_code[0]?.code
  )?.toUpperCase();
  const inviteLink = variant.getLink(inviteCode, memberType);
  const isInviteActionDisabled = isFetchingInviteCode || isRotating;
  const isRefreshDisabled = isInviteActionDisabled || hasRefreshed;

  const onPressRefresh = async () => {
    if (isRefreshDisabled) {
      return;
    }

    const { data, error } = await rotateInviteCode({
      entityId: entity.id,
      kind: entity.kind,
    });
    const nextInviteCode = data?.rotate_invite_code[0]?.code;

    if (error || !nextInviteCode) {
      setToast({
        text: 'Could not generate new code',
        icon: 'close-outline',
        variant: 'error',
      });
      return;
    }

    setRotatedInviteCode(nextInviteCode);
    setHasRefreshed(true);
    setToast({
      text: 'Generated New Codes & Links',
      icon: 'checkmark-outline',
      variant: 'success',
    });
  };

  return (
    <>
      <Stack.Screen
        options={{
          headerRight: () => (
            <Header.End>
              <Header.Button
                accessibilityLabel="Reset invite code"
                disabled={isRefreshDisabled}
                onPress={onPressRefresh}
                testID={`${variant.testIDPrefix}InviteResetButton`}
              >
                <Icon name="refresh-outline" />
              </Header.Button>
            </Header.End>
          ),
        }}
      />
      <InviteShareOptions
        code={inviteCode}
        link={inviteLink}
        disabled={isInviteActionDisabled}
        testIDPrefix={variant.testIDPrefix}
        setToast={setToast}
      >
        {entity.kind === 'team' && (
          <Box p="lg">
            <MemberTypeSelector
              testIDPrefix={`${variant.testIDPrefix}InviteMemberType`}
              value={memberType}
              onValueChange={setMemberType}
            />
          </Box>
        )}
      </InviteShareOptions>
    </>
  );
}
